import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import Swal from "sweetalert2";
import { clientActions } from "../../sagas/clientSlice";
import Select from "react-select";
import { productActions } from "../../sagas/productSlice";
import * as Yup from 'yup'; // Import Yup for validation
import CommandeLine from "./CommandeLine";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { formatPrice } from "../../helpers/utility";
import { reglementActions } from "../../sagas/reglementSlice";
import { FaArrowDown, FaArrowUp, FaBalanceScale, FaCalendarAlt, FaShieldAlt } from "react-icons/fa";
import { commercialActions } from "../../sagas/commercialSlice";

const initForm = {
    clientId: null,
    chantierId: null,
    lignes: [],
    dateLivraison: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
    dateOperation: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
    addresseLivraison: '',
    totalAmountHt: 0,
    totalAmountTtc: 0,
    totalAmountTva: 0,
    status: 'EN_PREPARATION',
    entrepotId: null,
    transporteurId: null,
    modeReglement: "-1",
};

const BonLivraisonForm = ({ onClose, bonLivraison }) => {
    const { t } = useTranslation('translation');
    const [formState, setForm] = useState(initForm);
    const dispatch = useDispatch();
    const clients = useSelector(state => state.clients.clients);
    const products = useSelector(state => state.products.products);
    const [currentPage, setCurrentPage] = useState(1);
    const { entrepots } = useSelector(state => state.entrepots);
    const { transporteurs } = useSelector(state => state.transporteurs);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const { list: commercialList, loading: commercialLoading } = useSelector((state) => state.commercials); // Adjust state path as needed

    const isClosed = bonLivraison && bonLivraison.status === 'LIVRE';

    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });

    useEffect(() => {
        if (bonLivraison) {
            setForm({ ...bonLivraison });
        } else {
            setForm(initForm);
        }

        dispatch(entrepotActions.list());
        dispatch(transporteurActions.list());
    }, [bonLivraison]);

    useEffect(() => {
        dispatch(commercialActions.list({ query: '' })); // Fetch commercials on component mount
    }, []);

    const commercialOptions = commercialList?.map(com => ({
        value: com.id,
        label: com.fullName,
    })) ?? [];

    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number()
            .required(t('bonLivraison.clientRequired')), // Ensure a client is selected
        entrepotId: Yup.number()
            .required(t('bonLivraison.entrepotRequired')), // Ensure an entrepot is selected
        // transporteurId: Yup.number()
        //     .required(t('bonLivraison.transporteurRequired')), // Ensure a transporteur is selected
        lignes: Yup.array().min(1, t('bonLivraison.atLeastOneItem')), // Ensure at least one item is added
        transporteurId: Yup.number().required(t('reception.transporteurMissing')),


    });

    const formik = useFormik({
        initialValues: {
            ...formState,
            clientId: bonLivraison ? bonLivraison.clientId : null,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            if (!values.transporteurId) {
                Swal.fire({
                    icon: 'warning',
                    title: t('sale.errorTitle'),
                    text: t('sale.transportRequired')
                });
                return;
            }

            Swal.fire({
                title: t('purchase.confirmation'),
                text: t('purchase.saveOrConfirmText'),
                icon: 'question',
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: t('common.saveAndConfirm'),
                denyButtonText: t('common.save'),
                cancelButtonText: t('common.cancel'),

            }).then((result) => {
                if (result.isConfirmed) {
                    if (bonLivraison) {
                        let payload = {
                            id: bonLivraison.id,
                            data: { ...values, status:"LIVRE", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.updated'),
                                    text: t('bonLivraison.updatedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.update(payload));
                    } else {
                        let payload = {
                            data: { ...values, status:"LIVRE", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.saved'),
                                    text: t('bonLivraison.savedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.addBL(payload));
                    }
                } else if (result.isDenied) {                
                    if (bonLivraison) {
                        let payload = {
                            id: bonLivraison.id,
                            data: { ...values, status:"EN_PREPARATION", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.updated'),
                                    text: t('bonLivraison.updatedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.update(payload));
                    } else {
                        let payload = {
                            data: { ...values, status:"EN_PREPARATION", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.saved'),
                                    text: t('bonLivraison.savedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.addBL(payload));
                    }
                } else {

                }
            });


        },
    });



    useEffect(() => {
        // Charger la liste des clients si elle n'est pas déjà disponible

        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }

        if (!products || products.length === 0) {
            dispatch(productActions.findAll({
                search: "",
                page: currentPage - 1,
                size: 10,
            }));
        }

        if (clients && bonLivraison && selectedClient == null) {
            setForm({ ...bonLivraison });
            if (bonLivraison.chantierNom && bonLivraison.clientId) {
                const client = clients.find(c => c.id === bonLivraison.clientId);
                if (client) {
                    const chantier = client.chantiers.find(ch => ch.nom === bonLivraison.chantierNom);
                    setSelectedChantier(chantier ? { value: chantier.id, label: chantier.nom } : null);
                    setSelectedClient({
                        value: client.id,
                        label: client.typeClient === 'PARTICULIER' ? `${client.nom} ${client.prenom || ''}` : client.entreprise,
                        chantiers: client.chantiers,
                        solde: client.solde,
                        totalDebit: client.totalDebit,
                        totalCredit: client.totalCredit,
                    });

                }
            }
        }

    }, [bonLivraison, clients, dispatch]);

    useEffect(() => {
        if (clients && bonLivraison) {
            let client = clients.find(c => c.id === bonLivraison.clientId);
            dispatch(reglementActions.echeanceClient({ ...filters, clientId: client.id }));
            dispatch(reglementActions.cautionClient({ ...filters, clientId: client.id }));
        }
    }, [clients, bonLivraison])

    useEffect(() => {
        dispatch(clientActions.search({
            query: ""
        }));
    }, [selectedClient])


    const { values, getFieldProps, handleSubmit, setFieldValue, errors, touched } = formik;

    useEffect(() => {
        let client = clients.find(client => client.id === values.clientId);
        setFieldValue('addresseLivraison', client ? client.adresse : '');
    }, [values.clientId]);

    // Map clients to options for React-Select
    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        chantiers: client.chantiers || [],
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom}`
            : client.entreprise,
    })) ?? [];

    const entrepotOptions = entrepots?.map(entrepot => ({
        value: entrepot.id,
        label: entrepot.code + ' - ' + entrepot.libelle,
    })) ?? [];

    const transporteurOptions = transporteurs?.map(transporteur => ({
        value: transporteur.id,
        label: transporteur.code + ' - ' + transporteur.libelle,
    })) ?? [];

    // Calculate montantTotalTaxe and montantTotalTTC
    const montantTotalTaxe = values.montantTotalTaxe || 0;
    const montantTotalTTC = values.montantTotalTTC || 0;
    const montantTotalHT = values.montantTotalHT || 0;

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    return (
        <Container fluid>
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card>
                    <CardHeader>
                        <Row>
                            <Col md="3">
                                <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptions}
                                    value={clientOptions.find(option => option.value === values.clientId)}
                                    onChange={(selectedOption) => {
                                        setFieldValue('clientId', selectedOption.value);
                                        setSelectedClient(selectedOption);
                                        dispatch(reglementActions.echeanceClient({ ...filters, clientId: selectedOption.value }));
                                        dispatch(reglementActions.cautionClient({ ...filters, clientId: selectedOption.value }));
                                        setFilters(f => ({ ...f, clientId: selectedOption.value }));
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    isDisabled={isClosed}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                                {errors.clientId && touched.clientId && (
                                    <div className="text-danger small mt-1">{errors.clientId}</div>
                                )}
                            </Col>
                            <Col md="3">
                                <Label htmlFor="chantierId">{t('chantier.title', 'Chantier')}:</Label>
                                <Select
                                    id="chantierId"
                                    options={selectedClient?.chantiers?.map(chantier => ({
                                        value: chantier.id,
                                        label: chantier.nom
                                    })) || []}
                                    value={selectedChantier}
                                    onChange={(selectedOption) => {
                                        setSelectedChantier(selectedOption);
                                        setFieldValue('chantierId', selectedOption ? selectedOption.value : null);
                                    }}
                                    placeholder={t('chantier.select', 'Sélectionnez...')}
                                    isSearchable
                                    isDisabled={isClosed}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                                {errors.chantierId && touched.chantierId && (
                                    <div className="text-danger small mt-1">{errors.chantierId}</div>
                                )}
                            </Col>
                            <Col md="2">
                                <Label htmlFor="commercialId">{t('delivery.commercial')}:</Label>
                                <Select
                                    id="commercialId"
                                    options={commercialOptions}
                                    value={commercialOptions.find(option => option.value === values.commercialId)}
                                    onChange={(selectedOption) => {
                                        setFieldValue('commercialId', selectedOption.value);
                                    }}
                                    placeholder={t('delivery.selectCommercial')}
                                    isSearchable
                                    isDisabled={isClosed}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                                {errors.commercialId && touched.commercialId && (
                                    <div className="text-danger small mt-1">{errors.commercialId}</div>
                                )}
                            </Col>
                            <Col md="2">
                                <Label htmlFor="entrepotId">{t('bonLivraison.entrepot')}:</Label>
                                <Select
                                    id="entrepotId"
                                    options={entrepotOptions}
                                    value={entrepotOptions.find(option => option.value === values.entrepotId)}
                                    onChange={(selectedOption) => setFieldValue('entrepotId', selectedOption.value)}
                                    placeholder={t('bonLivraison.selectEntrepot')}
                                    isSearchable
                                    isDisabled={isClosed}
                                />
                                {errors.entrepotId && touched.entrepotId && (
                                    <div className="text-danger small mt-1">{errors.entrepotId}</div>
                                )}
                            </Col>
                            <Col md="2">
                                <Label htmlFor="transporteurId">{t('bonLivraison.transporteur')}:</Label>
                                <Select
                                    id="transporteurId"
                                    options={transporteurOptions}
                                    value={transporteurOptions.find(option => option.value === values.transporteurId)}
                                    onChange={(selectedOption) => setFieldValue('transporteurId', selectedOption.value)}
                                    placeholder={t('bonLivraison.selectTransporteur')}
                                    isSearchable
                                    isDisabled={isClosed}
                                />
                                {errors.transporteurId && touched.transporteurId && (
                                    <div className="text-danger small mt-1">{errors.transporteurId}</div>
                                )}
                            </Col>
                        </Row>
                        {selectedClient && (
                            <Row className="mt-2 g-2">
                                <Col>
                                    <Row className="g-2 align-items-stretch">
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaBalanceScale className="mb-1 text-success" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.balance')}</span>
                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                        <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                        <span className="fw-bold text-danger" style={{ fontSize: '0.9rem' }}>-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : (
                                                        <span className="fw-bold text-muted" style={{ fontSize: '0.9rem' }}>{formatPrice(0)}</span>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaArrowDown className="mb-1 text-danger" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalDebit')}</span>
                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalDebit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaArrowUp className="mb-1 text-primary" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalCredit')}</span>
                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalCredit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Card className="shadow-sm h-100 bg-warning bg-opacity-10 border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <FaCalendarAlt className="me-1 text-warning" size={16} />
                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.echeances')}</span>
                                                    </div>
                                                    <span className="fw-bold text-warning" style={{ fontSize: '0.9rem' }}>{echeances?.length || 0}</span>
                                                    <Button color="link" size="sm" className="p-0 fw-bold text-warning" style={{ fontSize: '0.7rem' }} onClick={() => setShowEcheanceModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Card className="shadow-sm h-100 bg-info bg-opacity-10 border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <FaShieldAlt className="me-1 text-info" size={16} />
                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.cautions')}</span>
                                                    </div>
                                                    <span className="fw-bold text-info" style={{ fontSize: '0.9rem' }}>{cautions?.length || 0}</span>
                                                    <Button color="link" size="sm" className="p-0 fw-bold text-info" style={{ fontSize: '0.7rem' }} onClick={() => setShowCautionModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        )}
                        <Row>

                            <Col md="3">
                                <Label htmlFor="dateLivraison">{t('bonLivraison.dateLivraison')}:</Label>
                                <Input
                                    type="date"
                                    id="dateLivraison"
                                    {...getFieldProps('dateLivraison')}
                                    className="form-control"
                                    disabled={isClosed}
                                />
                            </Col>
                            <Col md="3">
                                <Label htmlFor="dateOperation">{t('bonLivraison.dateOperation')}:</Label>
                                <Input
                                    type="date"
                                    id="dateOperation"
                                    {...getFieldProps('dateOperation')}
                                    className="form-control"
                                    disabled={isClosed}
                                />
                            </Col>
                            <Col md="6">
                                <Label htmlFor="addresseLivraison">{t('bonLivraison.addresseLivraison')}:</Label>
                                <Input
                                    type="textarea"
                                    id="addresseLivraison"
                                    {...getFieldProps('addresseLivraison')}
                                    className="form-control"
                                    disabled={isClosed}
                                />
                            </Col>
                        </Row>

                    </CardHeader>
                    <CardBody>
                        <Row>
                            <CommandeLine formik={formik} disabled={isClosed} />
                        </Row>
                        {errors.lignes && touched.lignes && (
                            <div className="text-danger small mt-1">{errors.lignes}</div>
                        )}
                        <Row className="mt-3 justify-content-end">
                            <Col md="4" className="text-end">
                                <div className="mb-2">
                                    <h5>{t('devis.totalHT')}: {montantTotalHT} DH</h5>
                                </div>
                                <div className="mb-2">
                                    <h5>{t('devis.totalTaxe')}: {montantTotalTaxe} DH</h5>
                                </div>
                                <div>
                                    <h5>{t('devis.totalTTC')}: {montantTotalTTC} DH</h5>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button type="submit" color="primary" className="me-2" disabled={isClosed}>
                                    {t('common.save')}
                                </Button>
                                <Button type="button" color="secondary" onClick={onClose}>
                                    {t('common.cancel')}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Form>
            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};
export default BonLivraisonForm;